import * as React from 'react'
import { FunctionComponent, ReactElement } from 'react'
import { DetailsRow, IColumn, IconButton, IDetailsRowProps } from '@fluentui/react'
import LegacyLocalDatePicker, { LocalDatePickerProps } from '../../../components/LegacyLocalDatePicker'
import { Formik, FormikConfig, useFormikContext } from 'formik'
import { RegistrationMutationDto, RegistrationOverviewDto } from '../../../config/registrationConfig'
import { RegistrationItemProps } from './RegistrationList'
import { makeStyles, tokens } from '@fluentui/react-components'
import FormikField from '../../Formik/FormikField'
import MoneyInput, { MoneyInputProps } from '../../Input/MoneyInput'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import FormikInputField from '../../Formik/FormikInputField'
import UserPicker, { UserPickerProps } from '../../TagPicker/UserPicker'

export const useRegistrationRowStyles = makeStyles({
    rate: {
        width: '78px',
        minWidth: 'auto',
    },
    rateInput: {
        width: '78px',
    },
    description: {
        maxWidth: '480px',
    },
    descriptionWithInfo: {
        gridTemplateColumns: 'min-content auto',
    },
    dateInput: {
        width: '48px',
    },
    userControl: {
        width: '70px',
        minWidth: '70px',
        maxWidth: '70px',
        padding: tokens.spacingHorizontalXS,

        '> input': {
            width: '10px',
            minWidth: '10px',
            maxWidth: '10px',
            height: '20px',
            padding: 0,
        },

        '> div': {
            padding: 0,
            height: '20px',
        },

        '> span': {
            right: tokens.spacingHorizontalXS,
            // ...shorthands.padding(tokens.spacingHorizontalXS, 0),
            minHeight: '20px',
            height: '20px',
        },
    },
    vat: {
        width: '96px',
        minWidth: 'auto',
    },
    vatInput: {
        width: '63px',
    },
    type: {
        width: '120px',
        minWidth: 'auto',
    },
    typeInput: {
        width: '86px',
    },
})

export type ModifyRegistrationRowProps<D extends RegistrationOverviewDto, M extends RegistrationMutationDto, I = unknown> = {
    listProps: RegistrationItemProps<I>
    rowProps: Omit<IDetailsRowProps, 'item'> & {
        item: Partial<D | RegistrationOverviewDto> & Pick<RegistrationOverviewDto, 'user'>
    }
    formikProps: FormikConfig<M>
}

export type ModifyRegistrationColumnProps<I = unknown> = {
    column?: IColumn
} & RegistrationItemProps<I>

type Props<D extends RegistrationOverviewDto, M extends RegistrationMutationDto, I> = {
    columns: FunctionComponent<ModifyRegistrationColumnProps<I>>
} & ModifyRegistrationRowProps<D, M, I>

export function ModifyRegistrationColumns<M extends RegistrationMutationDto, I = unknown>(props: ModifyRegistrationColumnProps<I>): ReactElement {
    const styles = useRegistrationRowStyles()
    const { values, setFieldValue, submitForm } = useFormikContext<M>()

    switch (props.column.key) {
        case 'user':
            return (
                <FormikComboboxField<M, UserPickerProps>
                    required
                    size="small"
                    fieldName={'userId'}
                    component={UserPicker}
                    componentProps={{
                        singleSelect: true,
                        getEntityTagLabel: user => user.shortCode,
                        styles: {
                            control: styles.userControl,
                        },
                    }}
                />
            )
        case 'date':
            return (
                <FormikField<LocalDatePickerProps, M>
                    required
                    fieldName={'date'}
                    component={LegacyLocalDatePicker}
                    componentProps={{
                        isListColumn: true,
                        onSelectDate: date => setFieldValue('date', date, false),
                        datePickerProps: {
                            placeholder: undefined,
                            className: styles.dateInput,
                        },
                    }}
                />
            )
        case 'description':
            return (
                <FormikInputField<M>
                    required
                    fieldName={'description'}
                    componentProps={{
                        size: 'small',
                        className: styles.description,
                    }}
                />
            )
        case 'rate':
            return (
                <FormikField<MoneyInputProps, M>
                    required
                    fieldName={'rate'}
                    component={MoneyInput}
                    componentProps={{
                        size: 'small',
                        currency: values.currency,
                        allowNegative: true,
                        onCommit: (_, value: string) => {
                            setFieldValue('rate', value)
                        },
                        className: styles.rate,
                        input: { className: styles.rateInput },
                    }}
                />
            )
        case 'actions':
            return <IconButton iconProps={{ iconName: 'Save' }} onClick={submitForm} />
    }

    return null
}

export default function ModifyRegistrationRow<D extends RegistrationOverviewDto, M extends RegistrationMutationDto, I = unknown>(
    props: Props<D, M, I>,
): ReactElement {
    return (
        <Formik<M> {...props.formikProps}>
            <DetailsRow
                {...props.rowProps}
                compact
                onRenderItemColumn={(_item, _index?: number, column?: IColumn) => <props.columns {...props.listProps} column={column} />}
            />
        </Formik>
    )
}
